import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useSpring, animated } from "react-spring";

import Icon from "@ats/src/components/shared/Icon";

const ACC_SPRING = { tension: 320, friction: 32, clamp: true };

function useReducedMotion(): boolean {
  const [reduce, setReduce] = React.useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false,
  );
  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduce;
}

/* Collapse — measures natural content height and springs to it */
interface CollapseProps {
  open: boolean;
  children: React.ReactNode;
}

const Collapse: React.FC<CollapseProps> = ({ open, children }) => {
  const innerRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = React.useState(0);
  const reduce = useReducedMotion();

  React.useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const measure = () => setHeight((h) => (h !== el.scrollHeight ? el.scrollHeight : h));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children]);

  const style = useSpring({
    height: open ? height : 0,
    opacity: open ? 1 : 0,
    immediate: reduce,
    config: ACC_SPRING,
  });

  return (
    <animated.div style={{ overflow: "hidden", height: style.height, willChange: "height" }}>
      <animated.div ref={innerRef} style={{ opacity: style.opacity }}>
        {children}
      </animated.div>
    </animated.div>
  );
};

/* AccordionRow — base expandable item (divider-separated) */
interface AccordionRowProps {
  leading?: React.ReactNode;
  label: React.ReactNode;
  defaultOpen?: boolean;
  first?: boolean;
  children: React.ReactNode;
}

export const AccordionRow: React.FC<AccordionRowProps> = ({
  leading,
  label,
  defaultOpen = false,
  first = false,
  children,
}) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <Styled.Row data-first={first}>
      <Styled.RowButton type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {leading != null && <Styled.RowLeading>{leading}</Styled.RowLeading>}
        <Styled.RowLabel>{label}</Styled.RowLabel>
        <Styled.RowChevron data-open={open}>
          <Icon name="chevron-down" />
        </Styled.RowChevron>
      </Styled.RowButton>
      <Collapse open={open}>
        <Styled.RowBody>{children}</Styled.RowBody>
      </Collapse>
    </Styled.Row>
  );
};

/* AccordionSection — subtitle + count group divider (organisational, not collapsible) */
interface AccordionSectionProps {
  label: React.ReactNode;
  count?: number;
  children: React.ReactNode;
}

export const AccordionSection: React.FC<AccordionSectionProps> = ({
  label,
  count,
  children,
}) => (
  <div>
    <Styled.SectionLabel>
      {label}
      {count != null && <span> · {count}</span>}
    </Styled.SectionLabel>
    {children}
  </div>
);

/* Accordion — outer bordered panel whose header toggles the whole body */
interface AccordionProps {
  title: React.ReactNode;
  count?: React.ReactNode;
  meta?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

export const Accordion: React.FC<AccordionProps> = ({
  title,
  count,
  meta,
  defaultOpen = false,
  children,
}) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <Styled.Container>
      <Styled.HeaderButton type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Styled.Title>{title}</Styled.Title>
        {count != null && <Styled.Count>{count}</Styled.Count>}
        <Styled.Spacer />
        {meta}
        <Styled.HeaderChevron data-open={open}>
          <Icon name="chevron-down" />
        </Styled.HeaderChevron>
      </Styled.HeaderButton>
      <Collapse open={open}>
        <Styled.Body>{children}</Styled.Body>
      </Collapse>
    </Styled.Container>
  );
};

export default Accordion;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Container = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: Accordion_Container;
    border: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    ${t.rounded.md}
    overflow: hidden;
    background-color: ${t.dark ? t.color.gray[800] : t.color.white};
  `;
});

Styled.HeaderButton = styled.button((props: any) => {
  const t: any = props.theme;
  return css`
    label: Accordion_HeaderButton;
    width: 100%;
    display: flex;
    align-items: center;
    gap: ${t.spacing[2]};
    ${[t.px(3), t.py(3)]}
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: ${t.dark ? t.color.gray[700] : t.color.gray[100]};
    }
    &:focus {
      outline: none;
    }
    &:focus-visible {
      outline: none;
      box-shadow: inset 0 0 0 2px ${t.dark ? t.color.gray[600] : t.color.gray[300]};
    }
  `;
});

Styled.Title = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: Accordion_Title;
    ${[t.text.sm, t.text.bold]}
    color: ${t.dark ? t.color.gray[200] : t.color.black};
    white-space: nowrap;
    flex-shrink: 0;
  `;
});

Styled.Count = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: Accordion_Count;
    ${t.text.xs}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[500]};
    white-space: nowrap;
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
    margin-top: 2px;
  `;
});

Styled.Spacer = styled.span`
  flex: 1;
`;

Styled.HeaderChevron = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: Accordion_HeaderChevron;
    ${t.ml(1)}
    display: inline-flex;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    transform-origin: center;
    transition: transform 0.2s ease;
    &[data-open="true"] {
      transform: rotate(180deg);
    }
  `;
});

Styled.Body = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: Accordion_Body;
    border-top: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    > :first-child > :first-child {
      border-top: none;
    }
  `;
});

Styled.SectionLabel = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: Accordion_SectionLabel;
    ${[t.text.xs, t.text.bold, t.px(3), t.pt(3), t.pb(2)]}
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    border-top: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
  `;
});

Styled.Row = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: Accordion_Row;
    border-top: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    &[data-first="true"] {
      border-top: none;
    }
  `;
});

Styled.RowButton = styled.button((props: any) => {
  const t: any = props.theme;
  return css`
    label: Accordion_RowButton;
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: ${t.spacing[2]};
    ${[t.px(3), t.py(3)]}
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: ${t.dark ? t.color.gray[700] : t.color.gray[100]};
    }
    &:focus {
      outline: none;
    }
    &:focus-visible {
      outline: none;
      box-shadow: inset 0 0 0 2px ${t.dark ? t.color.gray[600] : t.color.gray[300]};
    }
  `;
});

Styled.RowLeading = styled.span`
  margin-top: 1px;
  flex-shrink: 0;
  display: inline-flex;
`;

Styled.RowLabel = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: Accordion_RowLabel;
    flex: 1;
    ${t.text.sm}
    line-height: 1.4;
    color: ${t.dark ? t.color.gray[300] : t.color.black};
  `;
});

Styled.RowChevron = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: Accordion_RowChevron;
    margin-top: 1px;
    flex-shrink: 0;
    display: inline-flex;
    color: ${t.color.gray[500]};
    transform-origin: center;
    transition: transform 0.2s ease;
    &[data-open="true"] {
      transform: rotate(180deg);
    }
  `;
});

Styled.RowBody = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: Accordion_RowBody;
    ${[t.text.sm, t.pr(3), t.pb(3), t.pt(3)]}
    padding-left: 2.125rem;
    line-height: 1.55;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    text-wrap: pretty;
  `;
});
